import { CatFiveAffinitiesTable, SectionTable } from 'components';
import { DATA_SOURCES } from '@/config/constants';
import EntityHeroCard from '../shared/entity/EntityHeroCard';
import EntityPage from '../shared/entity/EntityPage';
import EntityTabs, { EntityTab } from '../shared/entity/EntityTabs';
import EntityTopTerms from '../shared/entity/EntityTopTerms';
import { GoogleChartCard } from '../shared/entity/chartCards';
import { GOOGLE_METADATA_BY_KEY } from 'utils/metrics';
import { isTikTokMarket } from '../shared/entityPredicates';
import ToggleWatch from 'components/Button/ToggleWatch';
import { TopKeywordsSection } from 'components/TopKeywordsSection';
import useGoogleEntity from '../shared/entity/useGoogleEntity';
import { useSelector } from 'react-redux';
import { useTrackBrandView } from 'utils/hooks/useTrackEntityView';
import { views } from '@/copy';

const { brand: copy } = views;

const METRICS = [GOOGLE_METADATA_BY_KEY.price_segmentation];

function GoogleBrandPage({ brandId, controller }) {
    // The geo reducer, not `useCurrentCountry`: it can hold the old market.
    const country = useSelector(({ geo }) => geo);
    const page = useGoogleEntity({
        entityType: 'brands',
        entityId: brandId,
        country,
        controller,
        minimal: false,
    });
    const { name, entity, row } = page;

    useTrackBrandView(DATA_SOURCES.GOOGLE, {
        brandId,
        brandName: name,
        countryName: country?.name || null,
    });

    const healthStats = row?.healthStats;
    const skipPrediction = isTikTokMarket(page);

    return (
        <EntityPage
            page={page}
            title={{ name, isLoading: page.entityQuery.isLoading }}
            actions={
                <ToggleWatch id={brandId} data={{ name, type: 'brand' }} />
            }
            space="1.5rem"
            sections={
                <EntityTopTerms
                    as={TopKeywordsSection}
                    exportType="search-query"
                    highlighted
                    paginateBy="brands"
                    dated={false}
                    controller={controller}
                />
            }
            tabs={
                <EntityTabs>
                    <EntityTab name="products">
                        <SectionTable
                            icon="product"
                            title={copy.sections.products.title}
                            subtitle={copy.sections.products.subtitle}
                            tooltipKey="brandProductsTitleHelp"
                            tooltipExample={null}
                            paginateBy="products"
                            countries={page.countryIds}
                            products={(entity || row || {}).products}
                            brands={brandId}
                            controller={controller}
                            name={name}
                            source="google"
                            filename={page.exportName('product', {
                                entity: name || 'brand',
                            })}
                        />
                    </EntityTab>
                    <EntityTab name="related searches">
                        <CatFiveAffinitiesTable
                            icon="affinity"
                            title={copy.sections.affinities.title}
                            subtitle={copy.sections.affinities.subtitle}
                            tooltipKey="brandAffinitiesTitleHelp"
                            tooltipExample="affinitiesExample"
                            brandId={brandId}
                            countryId={country ? country.id : null}
                            controller={controller}
                            name={name}
                            filename={page.exportName('related-search', {
                                entity: name || 'brand',
                            })}
                        />
                    </EntityTab>
                </EntityTabs>
            }
        >
            <div className="flex flex-col gap-3">
                <EntityHeroCard
                    healthStats={healthStats}
                    showPredicted={!skipPrediction}
                />
                <GoogleChartCard
                    metricList={METRICS}
                    healthStats={healthStats}
                    skipPrediction={skipPrediction}
                />
            </div>
        </EntityPage>
    );
}

GoogleBrandPage.displayName = 'GoogleBrand';

export default GoogleBrandPage;
